import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { Plus, Edit2, Trash2, Calendar, MapPin, Users, Lock, Crown } from 'lucide-react'
import PageHeader from '../../../components/PageHeader'

// ── Event type images ─────────────────────────────────────
import imgWhiteWedding  from '../../../assets/events/white_wedding.jpg'
import imgTraditional   from '../../../assets/events/traditional.jpg'
import imgReception     from '../../../assets/events/reception.jpg'
import imgBridalShower  from '../../../assets/events/bridal_shower.jpg'
import imgBachelorEve   from '../../../assets/events/bachelors_eve.jpg'
import imgIntroduction  from '../../../assets/events/introduction.jpg'
import imgThanksgiving  from '../../../assets/events/thanksgiving.jpg'
import imgOther         from '../../../assets/events/other.jpg'

const EVENT_IMAGES = {
  'White Wedding':          imgWhiteWedding,
  'Traditional / Engagement': imgTraditional,
  'Reception':              imgReception,
  'Bridal Shower':          imgBridalShower,
  "Bachelor's Eve":         imgBachelorEve,
  'Introduction':           imgIntroduction,
  'Thanksgiving':           imgThanksgiving,
}
function getEventImage(type) {
  return EVENT_IMAGES[type] || imgOther
}

const EVENT_TYPES = [
  'White Wedding','Traditional / Engagement','Reception',
  'Bridal Shower',"Bachelor's Eve",'Introduction','Thanksgiving','Other'
]

// ── Add / Edit Modal ──────────────────────────────────────
function EventModal({ event, onClose, onSaved, isPremium, existingCount }) {
  const { wedding } = useWedding()

  const [type, setType]   = useState(event?.type || 'White Wedding')
  const [date, setDate]   = useState(event?.date || '')
  const [venue, setVenue] = useState(event?.venue || '')
  const [saving, setSaving] = useState(false)
  const [strictlyInvited, setStrictlyInvited] = useState(event?.strictly_invited || false)
  const [err, setErr]     = useState('')

  const isCustomType = type !== '' && !EVENT_TYPES.slice(0,-1).includes(type)
  const atLimit = !isPremium && !event?.id && existingCount >= 1

  async function save() {
    if (!type.trim()) { setErr('Please enter an event type.'); return }
    if (!isPremium && !event?.id && existingCount >= 1) { setErr('Free plan allows 1 event. Upgrade for more.'); return }
    setSaving(true)
    if (event?.id) {
      await supabase.from('wedding_events').update({ type:type.trim(), date:date||null, venue:venue||null, strictly_invited:strictlyInvited }).eq('id',event.id)
    } else {
      await supabase.from('wedding_events').insert({ wedding_id:wedding.id, type:type.trim(), date:date||null, venue:venue||null, strictly_invited:strictlyInvited })
    }
    setSaving(false); await onSaved(); onClose()
  }

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
      <div className="card fade-up" style={{ width:'100%',maxWidth:460,padding:'2rem',position:'relative' }}>
        <button onClick={onClose} style={{ position:'absolute',top:14,right:14,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC',fontSize:'1.2rem' }}>✕</button>
        <h2 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.25rem',margin:'0 0 1.5rem' }}>
          {event?.id ? 'Edit Event' : 'Add New Event'}
        </h2>

        {atLimit ? (
          <div style={{ textAlign:'center',padding:'1.5rem 0' }}>
            <Crown size={26} color="#C4826A" style={{ marginBottom:'0.75rem' }}/>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.9rem',color:'#1A1A1A',margin:'0 0 0.4rem' }}>Free plan allows 1 event</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#6B6B6B' }}>Upgrade to Premium to add unlimited events.</p>
          </div>
        ) : (
          <div style={{ display:'flex',flexDirection:'column',gap:'1rem' }}>
            <div>
              <label className="label">Event Type</label>
              <select className="input-field"
                value={EVENT_TYPES.includes(type) ? type : 'Other'}
                onChange={e => { if(e.target.value==='Other') setType(''); else setType(e.target.value) }}>
                {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
              {(!EVENT_TYPES.includes(type) || type === '') && (
                <input className="input-field" placeholder="Type your event name…" value={type}
                  onChange={e=>setType(e.target.value)} autoFocus style={{ marginTop:'0.5rem' }}/>
              )}
            </div>
            <div><label className="label">Date</label><input className="input-field" type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div><label className="label">Venue / City</label><input className="input-field" placeholder="e.g. The Grand Ballroom, Lagos" value={venue} onChange={e=>setVenue(e.target.value)}/></div>
          </div>
        )}

        {err && <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#C4826A',marginTop:'0.75rem' }}>{err}</p>}

        {/* Strictly by invitation */}
        <label style={{ display:'flex', alignItems:'center', gap:'0.625rem', cursor:'pointer', padding:'0.625rem 0.75rem', background: strictlyInvited ? 'rgba(196,130,106,0.06)' : '#FAFAF8', borderRadius:'0.75rem', border:`1.5px solid ${strictlyInvited ? 'rgba(196,130,106,0.3)' : '#E5E0D8'}`, marginTop:'0.75rem' }}>
          <input type="checkbox" checked={strictlyInvited} onChange={e => setStrictlyInvited(e.target.checked)} style={{ width:15, height:15, accentColor:'#C4826A' }}/>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:600, color:'#1A1A1A' }}>Strictly by Invitation</span>
        </label>

        <div style={{ display:'flex',gap:'0.75rem',marginTop:'1.25rem' }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex:1 }}>Cancel</button>
          {!atLimit && <button className="btn-primary" onClick={save} disabled={saving} style={{ flex:1,justifyContent:'center' }}>{saving?'Locking that in…':event?.id?'Save Changes':'Add Event'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Event Card ────────────────────────────────────────────
function EventCard({ ev, onEdit, onDelete, navigate }) {
  const [confirmed, setConfirmed] = useState(0)
  const [totalGuests, setTotalGuests] = useState(0)
  const [anyInvited, setAnyInvited]   = useState(false)
  const [showDelete, setShowDelete]   = useState(false)
  const [hovering, setHovering]       = useState(false)
  const img = getEventImage(ev.type)

  useEffect(() => {
    supabase.from('guests').select('id,plus_one,rsvp_status,event_id,invited_event_ids')
      .eq('wedding_id', ev.wedding_id)
      .then(({ data }) => {
        if (!data) return
        // filter guests for this event
        const evGuests = data.filter(g => {
          const ids = g.invited_event_ids
          if (!ids || ids.length === 0) return true
          return ids.includes(ev.id) || g.event_id === ev.id
        })
        const countConf = evGuests
          .filter(g => g.rsvp_status === 'confirmed' || g.rsvp_status === 'attending')
          .reduce((s, g) => s + (g.plus_one ? 2 : 1), 0)
        const total = evGuests.reduce((s, g) => s + (g.plus_one ? 2 : 1), 0)
        const invited = data.some(g => g.rsvp_status && g.rsvp_status !== 'pending')
        setConfirmed(countConf)
        setTotalGuests(total)
        setAnyInvited(invited)
      })
  }, [ev.id, ev.wedding_id])

  function daysLabel() {
    if (!ev.date) return null
    const diff = Math.ceil((new Date(ev.date) - new Date()) / (1000*60*60*24))
    if (diff < -1) return { text:`Happened ${Math.abs(diff)} days ago`, color:'rgba(255,255,255,0.35)', bold:false }
    if (diff === 0) return { text:'Today 🎉', color:'#C4826A', bold:true }
    if (diff <= 7)  return { text:`In ${diff} days ⚡`, color:'#C4826A', bold:true }
    if (diff <= 30) return { text:`In ${diff} days`, color:'#C4826A', bold:false }
    return { text:`${diff} days away`, color:'rgba(255,255,255,0.45)', bold:false }
  }
  const countdown = daysLabel()

  // Guest pill text
  function guestPillText() {
    if (totalGuests === 0) return 'No guests added'
    if (!anyInvited) return 'Invites not sent'
    if (confirmed === totalGuests) return `${confirmed} / ${totalGuests} confirmed ✓`
    return `${confirmed} / ${totalGuests} confirmed`
  }

  // Event type badge colors
  const TYPE_BADGE = {
    'White Wedding':           { bg:'rgba(255,252,245,0.92)', color:'#2A1810' },
    'Traditional / Engagement':{ bg:'rgba(212,168,83,0.9)',   color:'#2A1810' },
    'Reception':               { bg:'rgba(155,175,155,0.9)',  color:'#1A3820' },
    'Bridal Shower':           { bg:'rgba(242,212,220,0.92)', color:'#6A1830' },
    "Bachelor's Eve":          { bg:'rgba(100,120,180,0.85)', color:'white'   },
    'Introduction':            { bg:'rgba(179,107,85,0.92)',  color:'white'   },
    'Thanksgiving':            { bg:'rgba(212,168,83,0.85)',  color:'#2A1810' },
    'Other':                   { bg:'rgba(180,170,165,0.85)', color:'#1A1A1A' },
    // legacy / alternate names
    'Engagement Party':        { bg:'rgba(242,212,200,0.9)',  color:'#7A3020' },
    'Registry / Court':        { bg:'rgba(214,232,216,0.9)',  color:'#1A4A20' },
    'After Party':             { bg:'rgba(80,60,100,0.88)',   color:'white'   },
    'Traditional':             { bg:'rgba(212,168,83,0.9)',   color:'#2A1810' },
  }
  const typeBadge = TYPE_BADGE[ev.type] || { bg:'rgba(180,170,165,0.82)', color:'#1A1A1A' }

  const formattedDate = ev.date
    ? new Date(ev.date).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
    : null

  return (
    <div
      onClick={() => !showDelete && navigate(`/dashboard/events/${ev.id}`)}
      onMouseEnter={()=>setHovering(true)}
      onMouseLeave={()=>setHovering(false)}
      style={{ borderRadius:18, overflow:'hidden', background:'#1A1A1A', border:'none', cursor:'pointer',
        transform: hovering ? 'translateY(-4px)' : 'none',
        boxShadow: hovering ? '0 12px 40px rgba(0,0,0,0.15)' : 'none',
        transition:'transform 0.2s ease, box-shadow 0.2s ease',
        display:'flex', flexDirection:'column', position:'relative',
        opacity: showDelete ? 0.85 : 1,
      }}>

      {/* Image area */}
      <div style={{ width:'100%', height:150, overflow:'hidden', flexShrink:0, position:'relative' }}>
        <img src={img} alt={ev.type}
          style={{ width:'100%', height:'100%', objectFit:'cover', display:'block',
            transform: hovering ? 'scale(1.05)' : 'none', transition:'transform 0.4s' }}/>

        {/* Event type badge — top left */}
        {ev.type && (
          <span style={{ position:'absolute', top:10, left:10, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', padding:'3px 9px', borderRadius:100, background:typeBadge.bg, color:typeBadge.color, backdropFilter:'blur(4px)' }}>
            {ev.type}
          </span>
        )}



        {/* Manage Event label on hover */}
        <div style={{ position:'absolute', bottom:10, right:10, opacity: hovering ? 1 : 0, transition:'opacity 0.2s', pointerEvents:'none' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'white', fontWeight:600 }}>Manage Event →</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:'0.875rem 1rem', flex:1 }}>
        {/* Top row */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'0.3rem', gap:'0.5rem' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.95rem', color:'white', margin:'0 0 0.2rem', lineHeight:1.3 }}>{ev.type}</p>
            {countdown && (
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:countdown.color, margin:0, fontWeight: countdown.bold ? 700 : 400 }}>
                {countdown.text}
              </p>
            )}
          </div>
          <div style={{ display:'flex', gap:'5px', flexShrink:0 }} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>onEdit(ev)}
              style={{ width:30,height:30,borderRadius:8,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(196,130,106,0.18)',color:'#e8a88a',transition:'background 0.12s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(196,130,106,0.32)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(196,130,106,0.18)'}>
              <Edit2 size={13}/>
            </button>
            <button onClick={()=>setShowDelete(true)}
              style={{ width:30,height:30,borderRadius:8,border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(226,75,74,0.18)',color:'#f09595',transition:'background 0.12s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(226,75,74,0.3)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(226,75,74,0.18)'}>
              <Trash2 size={13}/>
            </button>
          </div>
        </div>

        {/* Tags */}
        <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginTop:'0.5rem' }}>
          {formattedDate && (
            <span style={{ display:'flex',alignItems:'center',gap:4,background:'rgba(255,255,255,0.07)',border:'0.5px solid rgba(255,255,255,0.14)',borderRadius:20,padding:'3px 9px',fontSize:'0.72rem',color:'rgba(255,255,255,0.7)' }}>
              <Calendar size={11}/> {formattedDate}
            </span>
          )}
          {ev.venue && (
            <span style={{ display:'flex',alignItems:'center',gap:4,background:'rgba(255,255,255,0.07)',border:'0.5px solid rgba(255,255,255,0.14)',borderRadius:20,padding:'3px 9px',fontSize:'0.72rem',color:'rgba(255,255,255,0.7)',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
              <MapPin size={11}/> {ev.venue}
            </span>
          )}
          <span style={{ display:'flex',alignItems:'center',gap:4,background:'rgba(155,175,155,0.18)',border:'0.5px solid rgba(155,175,155,0.35)',borderRadius:20,padding:'3px 9px',fontSize:'0.72rem',color:'#9BAF9B',fontWeight:600 }}>
            <Users size={11}/> {guestPillText()}
          </span>
        </div>
      </div>

      {/* Delete confirmation bar */}
      {showDelete && (
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(20,10,8,0.95)', padding:'0.875rem 1rem', borderTop:'1px solid rgba(226,75,74,0.3)',
            animation:'slideUp 0.25s ease-out' }}>
          <style>{`@keyframes slideUp { from { transform:translateY(100%); opacity:0 } to { transform:translateY(0); opacity:1 } }`}</style>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'rgba(255,255,255,0.8)', margin:'0 0 0.625rem', lineHeight:1.4 }}>
            Delete {ev.type}? This cannot be undone.
          </p>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={()=>setShowDelete(false)}
              style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'rgba(255,255,255,0.7)', background:'rgba(255,255,255,0.1)', border:'none', borderRadius:100, padding:'0.5rem', cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={()=>{ setShowDelete(false); onDelete(ev.id, ev.type) }}
              style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'white', background:'rgba(220,38,38,0.8)', border:'none', borderRadius:100, padding:'0.5rem', cursor:'pointer' }}>
              Delete Event
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function EventsPage() {
  const { wedding, events, refreshWedding, isPremium } = useWedding()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)

  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>One moment, pulling up your plans…</p>
    </div>
  )

  async function deleteEvent(id, name) {
    // Delete guests ONLY attached to this event (not shared with others)
    const { data: allGuests } = await supabase.from('guests').select('id, invited_event_ids')
      .eq('wedding_id', wedding.id)
    if (allGuests) {
      const toDelete = allGuests.filter(g => {
        const ids = g.invited_event_ids || []
        // Guest is attached to ONLY this event (exactly 1 event, and it's this one)
        return ids.length === 1 && ids.includes(id)
      }).map(g => g.id)
      if (toDelete.length > 0) {
        await supabase.from('guests').delete().in('id', toDelete)
      }
      // For guests attached to this event AND others, remove this event from their list
      const toUpdate = allGuests.filter(g => {
        const ids = g.invited_event_ids || []
        return ids.length > 1 && ids.includes(id)
      })
      for (const g of toUpdate) {
        await supabase.from('guests').update({
          invited_event_ids: g.invited_event_ids.filter(eid => eid !== id)
        }).eq('id', g.id)
      }
    }
    await supabase.from('wedding_events').delete().eq('id', id)
    await refreshWedding()
  }

  function openAdd() { setEditingEvent(null); setShowModal(true) }
  function openEdit(ev) { setEditingEvent(ev); setShowModal(true) }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>

      {/* Header */}
      <PageHeader
        title="My Events"
        subtitle={`Every celebration of your love${events.length ? ` · ${events.length} event${events.length!==1?'s':''} so far` : ''}. Tap any card to manage it.`}
        right={
          <button data-tour="add-event-btn" className="btn-primary" onClick={openAdd} style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
            <Plus size={15}/>
            {isPremium ? 'Add Event' : events.length===0 ? 'Add Event' : <><Lock size={13}/> Add Event</>}
          </button>
        }
      />

      {/* Free plan note */}
      {!isPremium && events.length >= 1 && (
        <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.875rem', padding:'0.75rem 1rem', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>
            <Crown size={13} color="#C4826A" style={{ marginRight:4 }}/> Free plan includes 1 event. Upgrade for unlimited.
          </p>
          <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ fontSize:'0.75rem', padding:'0.35rem 0.875rem' }}>Upgrade</button>
        </div>
      )}

      {/* Grid */}
      {events.length === 0 ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'1rem 0' }}>
          <div style={{ background:'#FEFCFA', border:'1px solid #E8E0D8', borderRadius:'1.25rem', padding:'3rem 2rem', textAlign:'center', maxWidth:340, width:'100%' }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ marginBottom:'1.25rem' }}>
              <circle cx="24" cy="24" r="20" stroke="#B36B55" strokeWidth="1.5" fill="none"/>
              <circle cx="24" cy="24" r="12" stroke="#B36B55" strokeWidth="1" fill="none" opacity="0.5"/>
              <path d="M24 8 C24 8 28 14 28 20 C28 23.3 26.2 26.2 24 28 C21.8 26.2 20 23.3 20 20 C20 14 24 8 24 8Z" stroke="#B36B55" strokeWidth="1.2" fill="rgba(179,107,85,0.08)"/>
              <path d="M8 24 C8 24 14 20 20 20 C23.3 20 26.2 21.8 28 24 C26.2 26.2 23.3 28 20 28 C14 28 8 24 8 24Z" stroke="#B36B55" strokeWidth="1.2" fill="rgba(179,107,85,0.08)"/>
              <path d="M40 24 C40 24 34 28 28 28 C24.7 28 21.8 26.2 20 24 C21.8 21.8 24.7 20 28 20 C34 20 40 24 40 24Z" stroke="#B36B55" strokeWidth="1.2" fill="rgba(179,107,85,0.08)"/>
            </svg>
            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>Your events will appear here</h3>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:'0 0 1.5rem', lineHeight:1.6 }}>Add your first event to start planning your perfect wedding day</p>
            <button className="btn-primary" onClick={openAdd} style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem' }}>
              <Plus size={15}/> Add Your First Event
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'1rem' }}>
          {events.map(ev => (
            <EventCard key={ev.id} ev={ev} onEdit={openEdit} onDelete={deleteEvent} navigate={navigate}/>
          ))}
        </div>
      )}

      {showModal && (
        <EventModal
          event={editingEvent}
          onClose={()=>{ setShowModal(false); setEditingEvent(null) }}
          onSaved={refreshWedding}
          isPremium={isPremium}
          existingCount={events.length}
        />
      )}
    </div>
  )
}
